import { Box, Icon, MenuItem, Skeleton, Stack } from '@mui/material';
import { School, SyncUserSchoolRelation } from '@schooly/api';
import { ArrowSchoolSelectIcon, CheckIcon } from '@schooly/style';
import React, { FC, useCallback, useState } from 'react';

import { SchoolSelectMenu, SchoolSelectStyled } from './SchoolSelect.styled';
import { SchoolSelectItem } from './SchoolSelectItem';
import { SHARED } from '@schooly/constants';

type SchoolSelectProps = {
  currentSchool?: School;
  schools: SyncUserSchoolRelation[];
  isLoading: boolean;
  isCollapsed?: boolean;
  onChangeSchool: (id: string) => void;
};

export const SchoolSelect: FC<SchoolSelectProps> = ({
  isCollapsed,
  onChangeSchool,
  isLoading,
  currentSchool,
  schools,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleMainClick = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuItemClick = useCallback(
    (school: School) => async () => {
      handleClose();
      onChangeSchool(school.id);
    },
    [handleClose, onChangeSchool],
  );

  if (!schools?.length) {
    return null;
  }

  if (!currentSchool || isLoading) {
    return (
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          mx: 1.5,
          pt: 1,
          pb: 1.75,
        }}
      >
        <Stack>
          <Skeleton width={36} height={36} variant="circular" />
        </Stack>
        {!isCollapsed && <Skeleton height={15} variant="rectangular" />}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        mx: isCollapsed ? 0 : 1.5,
        pb: 1.75,
      }}
    >
      {currentSchool && (
        <SchoolSelectStyled
          sx={{ justifyContent: isCollapsed ? 'center' : undefined }}
          data-test-id={SHARED.SCHOOL_SELECT}
          onClick={handleMainClick}
        >
          <SchoolSelectItem hideName={isCollapsed} school={currentSchool} />

          {!isCollapsed && (
            <Icon sx={{ display: 'flex', color: (theme) => theme.palette.text.secondary }}>
              <ArrowSchoolSelectIcon />
            </Icon>
          )}
        </SchoolSelectStyled>
      )}

      {currentSchool && (
        <SchoolSelectMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={handleClose}>
            <SchoolSelectStyled>
              <SchoolSelectItem school={currentSchool} />

              <Icon sx={{ '&&&': { color: (theme) => theme.palette.primary.main } }}>
                <ArrowSchoolSelectIcon />
              </Icon>
            </SchoolSelectStyled>
          </MenuItem>

          {schools.map((school_relation) => {
            const isCurrentSchool = school_relation.school.id === currentSchool?.id;

            return (
              <MenuItem
                key={school_relation.school.id}
                onClick={handleMenuItemClick(school_relation.school)}
                sx={{ gap: 1 }}
              >
                <SchoolSelectItem
                  school={school_relation.school}
                  color={isCurrentSchool ? 'primary.main' : undefined}
                  subitem
                />

                {isCurrentSchool && (
                  <Icon
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                    }}
                  >
                    <CheckIcon />
                  </Icon>
                )}
              </MenuItem>
            );
          })}
        </SchoolSelectMenu>
      )}
    </Box>
  );
};
